import { ApolloCache, Reference } from '@apollo/client';
import {
  AiDraftListItemFragment,
  AiDraftsDocument,
  AiDraftsQuery,
} from 'lib-knowledge-management-common/graphql';

/** The API caps `Limit` at 50. */
export const suggestionsPageSize = 25;

/**
 * An `aiDrafts` field as the cache holds it: the drafts themselves are
 * normalised out to references, the pagination stays inline.
 */
type CachedDrafts = {
  results?: readonly Reference[];
  pagination?: { total?: number };
};

/**
 * Cuts a draft from every cached queue it is in, and drops the total with it.
 *
 * Cut rather than refetched: `AiDrafts` is watched with `{limit: 25, offset:
 * 0}` and further pages exist only through `fetchMore`'s merge, so re-running
 * the operation would answer with the first page alone and throw a reviewer who
 * had scrolled back to the top of the queue.
 *
 * `modify` on the field name reaches every set of variables it was asked with,
 * so the draft leaves the list and any search then in effect. A list it was
 * never in is left untouched, which makes this safe to run twice — as it is,
 * when the web socket echoes a reviewer's own approve or discard.
 */
export function removeFromQueue(cache: ApolloCache<unknown>, id: string): void {
  cache.modify<{ aiDrafts: CachedDrafts }>({
    fields: {
      aiDrafts(existing, { readField }) {
        // Nothing to edit if the field is held as a bare reference.
        if (!('results' in existing) || !existing.results) return existing;

        const results = existing.results.filter(
          (draft) => readField('id', draft) !== id,
        );
        if (results.length === existing.results.length) return existing;

        return {
          ...existing,
          results,
          pagination: {
            ...existing.pagination,
            total: Math.max(0, (existing.pagination?.total ?? 1) - 1),
          },
        };
      },
    },
  });
}

/**
 * The unsearched queue, with exactly the variables `useSuggestions` watches it
 * with. `searchQuery` is left off rather than set to undefined so the two agree
 * on the cache key.
 */
const unsearchedQueue = {
  query: AiDraftsDocument,
  variables: { limit: suggestionsPageSize, offset: 0 },
};

/** The backend's order: `COALESCE(modified_at, created_at) DESC, id DESC`. */
function comesBefore(
  a: AiDraftListItemFragment,
  b: AiDraftListItemFragment,
): boolean {
  const activityA = a.modifiedAt ?? a.createdAt;
  const activityB = b.modifiedAt ?? b.createdAt;
  if (activityA !== activityB) return activityA > activityB;
  return a.id > b.id;
}

/**
 * Puts a draft where the backend's order has it in the unsearched queue: a new
 * draft, and one just revised, at the top; one whose change left its activity
 * time alone, where it was. A draft already listed moves rather than being
 * listed twice, so the total only grows for one that was not there. That may
 * be a draft from a page not fetched yet rather than a new one, which the
 * queue cannot tell apart; the total is then one too high until a refetch.
 *
 * A draft that sorts after every row loaded so far, while more remain, belongs
 * to a page not yet fetched, and is left for `fetchMore` to bring in.
 *
 * Searches are left alone: whether the draft matches one is the backend's to
 * say. So is a queue nobody has loaded, which reads the draft from the network
 * when it is first shown.
 */
export function placeInQueue(
  cache: ApolloCache<unknown>,
  draft: AiDraftListItemFragment,
): void {
  cache.updateQuery<AiDraftsQuery>(unsearchedQueue, (queue) => {
    if (!queue?.aiDrafts) return undefined;

    const { results, pagination } = queue.aiDrafts;
    const others = results.filter(({ id }) => id !== draft.id);
    const wasListed = others.length < results.length;

    const index = others.findIndex((other) => comesBefore(draft, other));
    const at = index === -1 ? others.length : index;
    const onALaterPage = at === others.length && pagination.hasMore;

    const placed = onALaterPage
      ? others
      : [...others.slice(0, at), draft, ...others.slice(at)];

    // Nothing moved: leave the query as it is rather than rewrite it.
    if (
      placed.length === results.length &&
      placed.every(({ id }, i) => id === results[i].id)
    ) {
      return undefined;
    }

    // Moving within the queue, or onto a page not fetched yet, leaves the count
    // as it is; only a draft that was not listed can be one more.
    const added = !wasListed && placed.length > others.length;
    const total = pagination.total + (added ? 1 : 0);

    return {
      ...queue,
      aiDrafts: {
        ...queue.aiDrafts,
        results: placed,
        pagination: { ...pagination, total },
      },
    };
  });
}

/** How long a lock is trusted without hearing it was released. */
export const lockTimeoutMs = 5 * 60_000;

/** Writes the lock onto the cached draft. A draft not in the cache is left alone. */
export function setDraftLocked(
  cache: ApolloCache<unknown>,
  id: string,
  isLocked: boolean,
): void {
  cache.modify({
    id: cache.identify({ __typename: 'AiDraft', id }),
    fields: { isLocked: () => isLocked },
  });
}
